import { ReadonlyHeaders } from 'next/dist/server/web/spec-extension/adapters/headers';

import { isServer } from 'src/shared/utils/index';
import { envEnum, getEnv } from 'src/shared/utils/crossEnv';
import { getBaseUrl } from 'src/shared/utils/getBaseUrl';

import { RequestMethod } from 'src/dal/config/requestMethod';
import { IRequestModel, IRequestHandlerOptions, RequestModelOutput, TPassedHeaders } from './RequestModel.typed';

const HEADER_NAMES_PROD = ['x-user-id', 'x-host'];
const HEADER_NAMES_DEV = ['x-guest-key', 'x-user-authenticated', 'x-user-id', 'x-host', 'cookie'];

const isReadonlyHeaders = (headers: TPassedHeaders): headers is ReadonlyHeaders =>
  'get' in headers && typeof headers.get === 'function';

const pickHeaders = (headers: ReadonlyHeaders, headerNames: string[]): Record<string, string> => {
  const pickedHeaders: Record<string, string> = {};

  headerNames.forEach((headerName) => {
    const value = headers.get(headerName);

    if (value) {
      pickedHeaders[headerName] = value;
    }
  });

  return pickedHeaders;
};

export const prepareHeaders = (passedHeaders?: TPassedHeaders): HeadersInit => {
  const env = getEnv(envEnum.NEXT_PUBLIC_ENVIRONMENT);
  const isProdLike = env.startsWith('prod') || env.startsWith('rc');
  const headerNames = isProdLike ? HEADER_NAMES_PROD : HEADER_NAMES_DEV;

  const baseHeaders: HeadersInit = isServer ? {} : { ...window.process?.headers };

  if (!passedHeaders) {
    return baseHeaders;
  }

  if (isReadonlyHeaders(passedHeaders)) {
    return isServer ? { ...baseHeaders, ...pickHeaders(passedHeaders, headerNames) } : baseHeaders;
  }

  return { ...baseHeaders, ...passedHeaders };
};

export const createRequestModel =
  ({ method = RequestMethod.GET, path }: IRequestModel) =>
  <T>(params?: IRequestHandlerOptions<T>): RequestModelOutput => {
    const { data = {}, options = {} } = params || {};
    const baseUrl = getBaseUrl();
    let url = `${baseUrl}${path}`;

    const requestInit: RequestInit = {
      method,
      ...options,
      headers: prepareHeaders(options?.headers),
    };

    if (method === RequestMethod.GET && data && typeof data === 'object' && Object.keys(data).length > 0) {
      const queryParams = new URLSearchParams();

      Object.entries(data).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null) {
              queryParams.append(key, String(item));
            }
          });
        } else if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });

      const queryString = queryParams.toString();

      if (queryString) {
        url = `${url}?${queryString}`;
      }
    } else if (method !== RequestMethod.GET && data) {
      requestInit.body = JSON.stringify(data);
      const currentHeaders = new Headers(requestInit.headers);

      if (!currentHeaders.has('Content-Type')) {
        currentHeaders.set('Content-Type', 'application/json');
      }
      requestInit.headers = currentHeaders;
    }

    return { url, requestInit };
  };
